import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { Grid, Link, Typography } from '@rbx/ui';
import { Locale, useTranslation } from '@rbx/intl';
import { useRouter } from 'next/router';
import { addLeadingSlash } from '@modules/utilities/utils/path';
import { getLocalePrefix } from '@modules/localization/implementations/translationResourceHelpers';
import LanguageMenu from '@modules/localization/components/LanguageMenu';
import FeedbackButton from '@modules/feedback/components/FeedbackButton';
import OpenSourceEditButton from '@modules/openSource/components/OpenSourceEditButton';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import { useRobloxAuthentication } from '@rbx/auth';
import groupsClient from '@modules/clients/groups';
import { apiReferenceBasePath } from '@modules/apiReference/page/constants/apiReferenceConstants';
import useAppHeadingStyles from './AppHeading.styles';
import { useAppConfig } from './AppConfigProvider';

export interface AppHeadingProps {
  title?: string;
  adornment?: ReactNode;
}

// NOTE (tchu, 2025-12-02): This is the new IA.
const AppHeading: FunctionComponent<React.PropsWithChildren<AppHeadingProps>> = ({
  title,
  adornment,
}) => {
  const {
    classes: { root },
  } = useAppHeadingStyles();
  const { enableNewLayout } = useAppConfig();
  const router = useRouter();
  const [englishUrl, setEnglishUrl] = useState<string | undefined>(undefined);
  const { isOpenSourced, locale, machineTranslated, contentDirFilePath } = useAppConfig();
  const { isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { isReady: isAllowFeedbackCheckReady, isDocsFeedbackAllowed } = useShareFeedbackProvider();
  const { translate, translateHTML } = useTranslation();
  useEffect(() => {
    const pathPrefix = [router.basePath, getLocalePrefix(Locale.English)].filter(Boolean).join('/');
    const pathname = router.asPath.replace(/\/\w{2}-\w{2}\//, '/');
    setEnglishUrl(`${addLeadingSlash(pathPrefix)}${pathname}`);
  }, [router]);

  const [isEmployee, setIsEmployee] = useState(false);
  useEffect(() => {
    groupsClient.isEmployee().then(setIsEmployee);
  }, []);

  const showOpenSourceEditButton =
    isOpenSourced || isEmployee || process.env.environment !== 'production';

  const { enableEngineReferenceRevamp } = useAppConfig();
  const isEngineReferencePage =
    typeof window !== 'undefined' && window.location.pathname.includes(apiReferenceBasePath);
  const useEngineReferenceButtons = (isEngineReferencePage && enableEngineReferenceRevamp) ?? false;

  if (enableNewLayout) {
    return (
      <Grid>
        <Grid container flexDirection='row' justifyContent='space-between' paddingBottom='24px'>
          <Grid>
            {title && (
              <Grid item>
                <Typography variant='h1' component='h1'>
                  {title}
                </Typography>
              </Grid>
            )}
            {adornment !== undefined && <Grid item>{adornment}</Grid>}
            <Grid item XSmall={12}>
              {locale !== Locale.English && isOpenSourced && !machineTranslated && (
                <Typography
                  variant='body1'
                  component='p'
                  color='disabled'
                  display='flex'
                  marginTop={2}>
                  *{translate('Description.TranslationComingSoon')}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid display='flex' item flexDirection='row'>
            <Grid item XSmall='auto'>
              <LanguageMenu engineRevampEnabled={useEngineReferenceButtons} />
            </Grid>
            {title && (
              <FeedbackButton
                pageTitle={title}
                show={!isAuthLoaded || !isAllowFeedbackCheckReady || isDocsFeedbackAllowed}
                engineRevampEnabled={useEngineReferenceButtons}
              />
            )}
            {showOpenSourceEditButton && contentDirFilePath && (
              <Grid item XSmall='auto'>
                <OpenSourceEditButton
                  contentDirFilePath={contentDirFilePath}
                  isEmployee={isEmployee}
                  machineTranslated={machineTranslated}
                  engineRevampEnabled={useEngineReferenceButtons}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {machineTranslated && (
          <Typography variant='body1' component='p' color='disabled' paddingBottom='24px'>
            *
            <span>
              {translateHTML('Description.BetaMachineTranslations', [
                {
                  opening: 'useEnglishLinkStart',
                  closing: 'useEnglishLinkEnd',
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content: (chunk) => (
                    <Link
                      sx={{
                        fontWeight: 'inherit !important',
                        textUnderlineOffset: '2px !important',
                      }}
                      color='inherit'
                      underline='always'
                      // eslint-disable-next-line react/forbid-component-props
                      href={englishUrl}>
                      {chunk}
                    </Link>
                  ),
                },
              ])}
            </span>
          </Typography>
        )}
      </Grid>
    );
  }

  return (
    <Grid
      className={title && !enableNewLayout ? root : undefined}
      container
      alignItems='center'
      justifyContent={typeof adornment === 'undefined' ? 'flex-start' : 'space-between'}>
      {title && (
        <Grid item>
          <Typography variant='h1' component='h1'>
            {title}
          </Typography>
        </Grid>
      )}
      {adornment !== undefined && <Grid item>{adornment}</Grid>}
      <Grid item XSmall={12}>
        {locale !== Locale.English && isOpenSourced && !machineTranslated && (
          <Typography variant='body1' component='p' color='disabled' display='flex' marginTop={2}>
            *{translate('Description.TranslationComingSoon')}
          </Typography>
        )}
        {machineTranslated && (
          <Typography variant='body1' component='p' color='disabled' display='flex' marginTop={2}>
            *
            <span>
              {translateHTML('Description.BetaMachineTranslations', [
                {
                  opening: 'useEnglishLinkStart',
                  closing: 'useEnglishLinkEnd',
                  // eslint-disable-next-line react/no-unstable-nested-components
                  content: (chunk) => (
                    <Link
                      sx={{
                        fontWeight: 'inherit !important',
                        textUnderlineOffset: '2px !important',
                      }}
                      color='inherit'
                      underline='always'
                      // eslint-disable-next-line react/forbid-component-props
                      href={englishUrl}>
                      {chunk}
                    </Link>
                  ),
                },
              ])}
            </span>
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default AppHeading;
